import { motion } from 'framer-motion';
import { BELIEF_ARCHITECTURE_PATTERNS } from '../../lib/api';

const BELIEF_LAYERS = [
  { id: 'core', label: 'Core Beliefs', y: 0 },
  { id: 'interpretation', label: 'Interpretation Layer', y: 1 },
  { id: 'emotional', label: 'Emotional Response', y: 2 },
  { id: 'behavior', label: 'Behavior Patterns', y: 3 },
  { id: 'outcomes', label: 'Life Outcomes', y: 4 }
];

export default function BeliefArchitectureMap({ beliefPattern, domainScores }) {
  const patternInfo = BELIEF_ARCHITECTURE_PATTERNS[beliefPattern?.primary_cluster];
  const secondaryInfo = beliefPattern?.secondary_cluster 
    ? BELIEF_ARCHITECTURE_PATTERNS[beliefPattern.secondary_cluster] 
    : null;

  const nodeColor = patternInfo?.color || '#6366F1';
  const secondaryColor = secondaryInfo?.color || '#8B5CF6';

  return (
    <div className="relative py-8">
      {/* Main Flow */}
      <div className="flex flex-col items-center gap-2">
        {BELIEF_LAYERS.map((layer, index) => (
          <motion.div
            key={layer.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            className="relative"
          >
            {/* Node */}
            <div 
              className="relative z-10 px-6 py-4 rounded-xl border-2 bg-card min-w-[200px] text-center"
              style={{ 
                borderColor: index === 0 ? nodeColor : 'hsl(var(--border))',
                boxShadow: index === 0 ? `0 0 20px ${nodeColor}30` : 'none'
              }}
            >
              <p className="text-xs font-mono text-muted-foreground mb-1">
                Layer {index + 1}
              </p>
              <p className="font-medium">{layer.label}</p>
              
              {/* Show pattern name at core beliefs level */}
              {index === 0 && patternInfo && (
                <p 
                  className="text-sm mt-2 font-medium"
                  style={{ color: nodeColor }}
                >
                  {patternInfo.name}
                </p>
              )}
            </div>

            {/* Connection Arrow */}
            {index < BELIEF_LAYERS.length - 1 && (
              <div className="flex justify-center py-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 24 }}
                  transition={{ delay: index * 0.1 + 0.1 }}
                  className="w-0.5 bg-border relative"
                >
                  <div 
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-0 h-0"
                    style={{
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: `8px solid hsl(var(--border))`
                    }}
                  />
                </motion.div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Supporting Patterns - Side Panel */}
      {patternInfo?.supporting && patternInfo.supporting.length > 0 && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="absolute top-8 right-0 md:right-8 w-48"
        >
          <div className="p-4 rounded-lg bg-secondary/30 border border-border/50">
            <p className="text-xs font-mono text-muted-foreground mb-3">Supporting Patterns</p>
            <div className="space-y-2">
              {patternInfo.supporting.map((pattern, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-2"
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: secondaryColor }}
                  />
                  <span className="text-xs">{pattern}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Legend */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-8 text-center"
      >
        <p className="text-xs text-muted-foreground">
          Beliefs at the core level cascade through interpretation, emotional response, 
          and behavior patterns to create life outcomes.
        </p>
      </motion.div>
    </div>
  );
}
